/**
 * ClarityContext.jsx - Global Clarity Mode State Management
 * 
 * CLARITY MODE = GLOBAL UI ENVIRONMENT
 * - Institutional: Full data density, technical terminology, all metadata visible
 * - Clear: Plain English, simplified views, inline examples
 * - Minimal: Essential info only, collapsed advanced panels
 * 
 * PRODUCT LAW:
 * - Clarity affects what Bulb shows first
 * - No scoring, no persuasion
 * - Facts-only presentation
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

// Clarity Mode values
export const CLARITY_MODES = {
  INSTITUTIONAL: 'institutional',
  CLEAR: 'clear',
  MINIMAL: 'minimal'
};

// Mode labels and descriptions
export const CLARITY_OPTIONS = [
  {
    id: CLARITY_MODES.INSTITUTIONAL,
    label: 'Institutional',
    shortLabel: 'Inst.',
    description: 'Full data density, technical terminology',
    icon: 'Building2'
  },
  {
    id: CLARITY_MODES.CLEAR,
    label: 'Clear',
    shortLabel: 'Clear',
    description: 'Plain English, simplified views',
    icon: 'Eye'
  },
  {
    id: CLARITY_MODES.MINIMAL,
    label: 'Minimal',
    shortLabel: 'Min.',
    description: 'Essential information only',
    icon: 'Minus'
  }
];

// Storage keys
const STORAGE_KEY = 'granullo_clarity_mode';

// Context
const ClarityContext = createContext(null);

/**
 * ClarityProvider - Provides global Clarity state
 */
export const ClarityProvider = ({ children }) => {
  // Initialize from localStorage with Institutional as default
  const [clarityMode, setClarityModeState] = useState(() => {
    if (typeof window === 'undefined') return CLARITY_MODES.INSTITUTIONAL;
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored && Object.values(CLARITY_MODES).includes(stored) 
      ? stored 
      : CLARITY_MODES.INSTITUTIONAL;
  });

  // Apply clarity class to document for CSS styling hooks
  useEffect(() => {
    if (typeof document === 'undefined') return;
    
    // Remove all clarity classes
    Object.values(CLARITY_MODES).forEach(mode => {
      document.documentElement.classList.remove(`clarity-${mode}`);
    });
    
    // Add current clarity class
    document.documentElement.classList.add(`clarity-${clarityMode}`);
    
    // Also set data attribute for CSS selectors
    document.documentElement.setAttribute('data-clarity', clarityMode);
  }, [clarityMode]);

  // Persist to localStorage and optionally to user profile
  const setClarityMode = useCallback((mode) => {
    if (!Object.values(CLARITY_MODES).includes(mode)) {
      console.warn('Invalid clarity mode:', mode);
      return;
    }
    setClarityModeState(mode);
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, mode);
    }
    // TODO: Also persist to user profile via API if authenticated
  }, []);

  // Helper functions for mode checking
  const isInstitutional = useMemo(() => clarityMode === CLARITY_MODES.INSTITUTIONAL, [clarityMode]);
  const isClear = useMemo(() => clarityMode === CLARITY_MODES.CLEAR, [clarityMode]);
  const isMinimal = useMemo(() => clarityMode === CLARITY_MODES.MINIMAL, [clarityMode]);

  // Get current mode option
  const currentOption = useMemo(() => 
    CLARITY_OPTIONS.find(opt => opt.id === clarityMode) || CLARITY_OPTIONS[0],
    [clarityMode]
  );

  // Context value
  const value = useMemo(() => ({
    clarityMode,
    setClarityMode,
    isInstitutional,
    isClear,
    isMinimal,
    currentOption,
    CLARITY_MODES,
    CLARITY_OPTIONS
  }), [clarityMode, setClarityMode, isInstitutional, isClear, isMinimal, currentOption]);

  return (
    <ClarityContext.Provider value={value}>
      {children}
    </ClarityContext.Provider>
  );
};

/**
 * useClarity - Hook to access Clarity state
 */
export const useClarity = () => {
  const context = useContext(ClarityContext);
  if (!context) {
    // Return safe defaults if used outside provider (for backward compat)
    return {
      clarityMode: CLARITY_MODES.INSTITUTIONAL,
      setClarityMode: () => {},
      isInstitutional: true,
      isClear: false,
      isMinimal: false,
      currentOption: CLARITY_OPTIONS[0],
      CLARITY_MODES,
      CLARITY_OPTIONS
    };
  }
  return context;
};

/**
 * ClarityGate - Conditionally render based on clarity mode
 * 
 * Usage:
 * <ClarityGate show="institutional">Technical content</ClarityGate>
 * <ClarityGate show="clear">Simplified content</ClarityGate>
 * <ClarityGate hide="minimal">Content hidden in minimal</ClarityGate>
 */
export const ClarityGate = ({ show, hide, children }) => {
  const { clarityMode } = useClarity();
  
  // Handle show prop (show only in specified mode(s))
  if (show) {
    const modes = Array.isArray(show) ? show : [show];
    if (!modes.includes(clarityMode)) return null;
  }
  
  // Handle hide prop (hide in specified mode(s))
  if (hide) {
    const modes = Array.isArray(hide) ? hide : [hide];
    if (modes.includes(clarityMode)) return null;
  }
  
  return children;
};

/**
 * withClarity - HOC for clarity-aware components
 */
export const withClarity = (Component) => {
  return function ClarityWrapped(props) {
    const clarity = useClarity();
    return <Component {...props} clarity={clarity} />;
  };
};

export default ClarityContext;
